import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { trackGTMEvent } from '@/components/GoogleTagManager';
import { Loader2 } from 'lucide-react';

export default function ComebackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');
  const [isAlreadyCompleted, setIsAlreadyCompleted] = useState(false);

  // Helper to create base64 token (compatible with useFunnelSession)
  const utf8ToBase64 = (str: string): string => {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16))
    )).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  useEffect(() => {
    const validateAndRedirect = async () => {
      const prospectId = searchParams.get('prospect_id');

      if (!prospectId) {
        console.error('[ComebackPage] No prospect_id in URL');
        navigate('/?error=invalid_comeback_link');
        return;
      }

      try {
        // Use edge function for secure validation
        const { data, error } = await supabase.functions.invoke('validate-comeback', {
          body: { prospect_id: prospectId }
        });

        if (error || !data?.valid) {
          // Check if signup already completed
          if (data?.error === 'already_completed') {
            console.log('[ComebackPage] Signup already completed');
            trackGTMEvent('comeback_already_completed', { prospect_id: prospectId });
            
            // Clean comeback data from localStorage to prevent banner from showing again
            localStorage.removeItem('comeback_prospect');
            localStorage.removeItem('funnel_session');
            console.log('[ComebackPage] Cleaned comeback data from localStorage');
            
            setIsAlreadyCompleted(true);
            setStatus('success'); // Show success UI with custom message
            setTimeout(() => {
              navigate('/?info=signup_completed');
            }, 2500);
            return;
          }
          
          console.error('[ComebackPage] Invalid comeback:', error);
          trackGTMEvent('comeback_link_invalid', { prospect_id: prospectId });
          navigate('/?error=expired_comeback_link');
          return;
        }

        // Create session object for funnel
        const sessionData = {
          email: data.email,
          prospect_id: prospectId,
          interests: data.interests || '',
          plan: 'monthly',
          timestamp: Date.now()
        };

        // Create token (compatible with useFunnelSession)
        const sessionToken = utf8ToBase64(JSON.stringify(sessionData));

        // Save session to localStorage
        localStorage.setItem('funnel_session', JSON.stringify(sessionData));

        // Save comeback discount data (for banner and pricing)
        localStorage.setItem('comeback_prospect', JSON.stringify({
          prospect_id: prospectId,
          discount_percentage: data.discount_percentage || 0,
          expires_at: data.expires_at,
          expired: data.expired,
          timestamp: Date.now()
        }));

        trackGTMEvent('comeback_link_valid', { 
          prospect_id: prospectId,
          discount: data.discount_percentage,
          redirect_to: data.redirect_to
        });

        setStatus('success');
        
        // Redirect with token in URL
        const redirectUrl = `${data.redirect_to}${data.redirect_to.includes('?') ? '&' : '?'}token=${sessionToken}`;
        
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 800);

      } catch (err) {
        console.error('[ComebackPage] Error:', err);
        setStatus('error');
        setTimeout(() => navigate('/?error=validation_error'), 1500);
      }
    };

    validateAndRedirect();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/20">
      <div className="text-center space-y-4 p-8">
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-newsletter-primary mx-auto" />
            <p className="text-muted-foreground animate-pulse">{t('funnel.comeback.loading')}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="text-4xl mb-2">❌</div>
            <p className="text-red-500 font-medium">{t('funnel.comeback.error')}</p>
            <p className="text-sm text-muted-foreground">{t('funnel.comeback.errorRedirecting')}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="text-4xl mb-2">✅</div>
            <p className="text-green-600 font-medium">
              {isAlreadyCompleted 
                ? t('funnel.comeback.alreadyCompleted') 
                : t('funnel.comeback.success')}
            </p>
            <p className="text-sm text-muted-foreground">{t('funnel.comeback.redirecting')}</p>
          </>
        )}
      </div>
    </div>
  );
}
